
import React from 'react';
import { motion } from 'framer-motion';
import { Code, Bolt, Zap, Sparkles, ArrowRight } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

const FeatureCard = ({ icon, title, description, delay }: FeatureCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="glass p-6 hover:bg-white/10 transition-all duration-300"
  >
    <div className="w-12 h-12 rounded-lg bg-brand-purple/20 flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-2 font-display">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
  </motion.div>
);

const Features = () => {
  return (
    <section className="py-20" id="features">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">
            <span className="bg-gradient-to-r from-brand-purple to-brand-blue bg-clip-text text-transparent">
              Supercharge
            </span> Your AI Development
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Stop spending hours crafting the perfect prompts. Let PromptAssist generate optimized instructions for your AI-powered IDE.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard 
            icon={<Code className="h-6 w-6 text-brand-purple" />}
            title="IDE-Optimized Prompts"
            description="Generate prompts specifically formatted for Cursor, Windsurf, V0, and VS Code Copilot that yield better results."
            delay={0.1}
          />
          
          <FeatureCard 
            icon={<Bolt className="h-6 w-6 text-brand-blue" />}
            title="Quick Generation"
            description="Describe your app once and get a comprehensive set of prompts ready to use in seconds."
            delay={0.2}
          />
          
          <FeatureCard 
            icon={<Zap className="h-6 w-6 text-brand-green" />}
            title="Prompt Enhancement"
            description="Not getting the results you want? Enhance your prompts with additional context and specifications."
            delay={0.3}
          />
          
          <FeatureCard 
            icon={<Sparkles className="h-6 w-6 text-yellow-500" />}
            title="Multi-Stage Development"
            description="Get prompts for each stage of development from setup to polish, ensuring a complete application."
            delay={0.4}
          />
          
          <FeatureCard 
            icon={<ArrowRight className="h-6 w-6 text-pink-500" />}
            title="Code Snippets"
            description="Include code examples and snippets in your prompts to guide the AI in the right direction."
            delay={0.5}
          />
          
          <FeatureCard 
            icon={<Sparkles className="h-6 w-6 text-indigo-400" />}
            title="Best Practices"
            description="Our prompts incorporate best practices and patterns for modern web development."
            delay={0.6}
          />
        </div>
      </div>
    </section>
  );
};

export default Features;
